// ═══════════════════════════════════════════════
//   STAFF — hiring minigame (replaces old carousel)
// ═══════════════════════════════════════════════

const { S, STAFF_APPLICANTS, STAFF_SLOTS, CONFIG, todayStr, scheduleSave, toast } = window;

// module-local session state — reset on each buildStaff() call (i.e. each login)
let _queue   = [];   // ordered list of applicants for this session
let _queueIdx = 0;   // current position in the queue
let _msgs    = [];   // chat messages for this session
let _waiting = false;
let _flipBadge = null;

// ── window opener ──────────────────────────────────────────────────────
function openStaff() {
    openWin('w-staff');
    // Only rebuild if no active interview session is showing
    // (staff-actions is present only during an active session).
    if (!document.getElementById('staff-actions')) {
        buildStaff();
    }
}

// ── public entry point (called from auth.js on login and by openStaff) ─
function buildStaff() {
    const today = todayStr();
    if (S.staffDailyDate !== today) {
        S.staffDailyDate  = today;
        S.staffDailyCount = 0;
        S.staffRejectedNames = [];
        scheduleSave();
    }

    _buildQueue();
    _queueIdx  = 0;
    _msgs      = [];
    _waiting   = false;
    _flipBadge = null;

    const closed = _isClosed();
    if (closed) {
        _renderClosed(closed);
    } else {
        _render();
        _pushAppMsg(_queue[0]);
    }
}

// ── queue helpers ──────────────────────────────────────────────────────
function _buildQueue() {
    const hiredNames  = new Set(Object.values(S.staffHiredSlots));
    const rejectedSet = new Set(S.staffRejectedNames);
    const pool = STAFF_APPLICANTS.filter(a => !hiredNames.has(a.name) && !rejectedSet.has(a.name));
    for (let i = pool.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [pool[i], pool[j]] = [pool[j], pool[i]];
    }
    _queue = pool;
}

// returns false if game can continue, or a string reason if it should stop
function _isClosed() {
    if (_queue.length === 0) return 'pool';

    const openSlots = STAFF_SLOTS.filter(s => !s.locked);
    if (Object.keys(S.staffHiredSlots).length >= openSlots.length) return 'full';

    if (_todayRemaining() <= 0) return 'daily';

    return false;
}

function _todayRemaining() {
    return Math.max(0, CONFIG.STAFF_DAILY_LIMIT - S.staffDailyCount);
}

function _currentApp() {
    return _queue[_queueIdx] || null;
}

// ── chat helpers ───────────────────────────────────────────────────────
function _pushMsg(msg) {
    _msgs.push(msg);
    _renderChat();
}

function _pushAppMsg(app) {
    if (!app) return;
    _pushMsg({ from: 'app', name: app.name, emoji: app.emoji, color: app.color, text: app.message });

    setTimeout(() => {
        if (!_currentApp() || _currentApp().name !== app.name) return;
        _pushMsg({ from: 'app', name: app.name, emoji: app.emoji, color: app.color, text: `Skills: ${app.tags.join(' · ')}` });
    }, 700);
}

// ── decision handler ───────────────────────────────────────────────────
function staffDecide(hire) {
    if (_waiting) return;
    const app = _currentApp();
    if (!app) return;

    const firstName = app.name.split(' ')[0];
    const reply = hire
        ? `Great news, ${firstName}! You're hired!! 🎉`
        : `Thanks ${firstName}! We'll keep your file on record 📋`;

    _pushMsg({ from: 'me', text: reply });

    if (hire) {
        const slotIdx = _findSlot(app.role);
        if (slotIdx >= 0) {
            S.staffHiredSlots[slotIdx] = app.name;
            scheduleSave();
        }
        toast(`${app.name} hired!`);
        if (window.triggerMission) window.triggerMission('staff_hire');
    } else {
        if (!S.staffRejectedNames.includes(app.name)) {
            S.staffRejectedNames.push(app.name);
        }
    }

    S.staffDailyCount++;
    scheduleSave();

    _waiting = true;
    _renderButtons();
    _renderBadges();

    setTimeout(() => {
        _queueIdx++;
        _waiting = false;

        const outOfQueue = _queueIdx >= _queue.length;
        const closed     = _isClosed();

        if (outOfQueue || closed) {
            _renderClosed(outOfQueue ? 'pool' : closed);
        } else {
            _render();
            _pushAppMsg(_currentApp());
        }
    }, 1100);
}

// find the best open slot for a given role
function _findSlot(role) {
    for (let i = 0; i < STAFF_SLOTS.length; i++) {
        const s = STAFF_SLOTS[i];
        if (!s.locked && !S.staffHiredSlots[i] && s.role === role) return i;
    }
    for (let i = 0; i < STAFF_SLOTS.length; i++) {
        const s = STAFF_SLOTS[i];
        if (!s.locked && !S.staffHiredSlots[i]) return i;
    }
    return -1;
}

// ── badge flip ─────────────────────────────────────────────────────────
function staffFlipBadge(idx) {
    _flipBadge = _flipBadge === idx ? null : idx;
    _renderBadges();
}

// ── release a hired staff member (re-opens that slot) ──────────────────
function staffDismiss(slotIdx) {
    delete S.staffHiredSlots[slotIdx];
    scheduleSave();
    toast('Staff member released — slot is open again!');
    buildStaff();
}

// ── renderers ──────────────────────────────────────────────────────────
function _render() {
    const el = document.getElementById('staff-body');
    if (!el) return;

    el.innerHTML = `
        <div class="staff-wrap">
            <div class="staff-header">
                <div class="staff-title">★ INTERVIEW CHAT</div>
                <div class="staff-counter" id="staff-counter"></div>
            </div>
            <div class="staff-strip" id="staff-strip"></div>
            <div class="staff-chat"  id="staff-chat"></div>
            <div class="staff-actions" id="staff-actions"></div>
            <div class="staff-badge-row">
                <div class="staff-badge-label">STAFF BADGES — tap to flip</div>
                <div class="staff-badges" id="staff-badges"></div>
            </div>
        </div>`;

    _renderCounter();
    _renderStrip();
    _renderChat();
    _renderButtons();
    _renderBadges();
}

function _renderCounter() {
    const el = document.getElementById('staff-counter');
    if (!el) return;
    const todayLeft  = _todayRemaining();
    const queueLeft  = Math.max(0, _queue.length - _queueIdx - 1);
    const waiting    = Math.min(queueLeft, Math.max(0, todayLeft - 1));
    el.innerHTML = `<b>${waiting}</b> still waiting`;
}

function _renderStrip() {
    const el = document.getElementById('staff-strip');
    if (!el) return;

    const todayLeft = _todayRemaining();
    const visible   = _queue.slice(0, Math.min(_queue.length, _queueIdx + todayLeft));
    const hiredNames = new Set(Object.values(S.staffHiredSlots));

    el.innerHTML = visible.map((a, i) => {
        const isCur   = i === _queueIdx;
        const isPast  = i < _queueIdx;
        const isHired = hiredNames.has(a.name);
        const bg = (isHired && isPast) ? '' : `background:${a.color};`;

        const dot  = isCur ? '<div class="staff-strip-dot"></div>' : '';
        const line = i < visible.length - 1
            ? `<div class="staff-strip-line${(isPast || isCur) ? ' active' : ''}"></div>`
            : '';

        const cls = ['staff-strip-item'];
        if (isCur) cls.push('cur');
        if (isPast && !isHired) cls.push('past');
        if (isHired && isPast) cls.push('hired');

        return `
            <div class="staff-strip-slot">
                <div class="${cls.join(' ')}" style="${bg}">
                    ${a.emoji}
                </div>
                ${dot}
            </div>${line}`;
    }).join('');
}

function _renderChat() {
    const el = document.getElementById('staff-chat');
    if (!el) return;

    const msgsHTML = _msgs.map(m => {
        if (m.from === 'app') {
            return `
                <div class="staff-msg staff-msg-app">
                    <div class="staff-avatar" style="background:${m.color}">${m.emoji}</div>
                    <div>
                        <div class="staff-msg-name">${m.name}</div>
                        <div class="staff-bubble-app">${m.text}</div>
                    </div>
                </div>`;
        }
        return `<div class="staff-msg staff-msg-me"><div class="staff-bubble-me">${m.text}</div></div>`;
    }).join('');

    const emptyHTML  = _msgs.length === 0 ? '<div class="staff-msg-empty">👋 Loading applications…</div>' : '';
    const typingHTML = _waiting ? `
        <div class="staff-typing">
            <span></span><span></span><span></span>
        </div>` : '';

    el.innerHTML = emptyHTML + msgsHTML + typingHTML;
    setTimeout(() => { el.scrollTop = el.scrollHeight; }, 60);
}

function _renderButtons() {
    const el = document.getElementById('staff-actions');
    if (!el) return;

    if (_waiting) {
        el.innerHTML = `<div class="staff-loading">loading next…</div>`;
        return;
    }
    el.innerHTML = `
        <button class="staff-btn staff-btn-pass" onclick="staffDecide(false)">✗ PASS</button>
        <button class="staff-btn staff-btn-hire" onclick="staffDecide(true)">★ HIRE</button>`;
}

function _renderClosed(reason) {
    const el = document.getElementById('staff-body');
    if (!el) return;

    let title, sub;
    if (reason === 'daily') {
        title = 'Hiring closed for today';
        sub   = 'Check back tomorrow — more applications await!';
    } else if (reason === 'full') {
        title = 'All positions filled!';
        sub   = 'Your konbini team is complete. New roles may open later.';
    } else {
        title = 'Hiring is closed';
        sub   = "We'll let you know when new applications open.";
    }

    el.innerHTML = `
        <div class="staff-wrap">
            <div class="staff-closed">
                <div class="staff-closed-emoji">📋</div>
                <div class="staff-closed-title">${title}</div>
                <div class="staff-closed-sub">${sub}</div>
            </div>
            <div class="staff-badge-row">
                <div class="staff-badge-label">STAFF BADGES — tap to flip</div>
                <div class="staff-badges" id="staff-badges"></div>
            </div>
        </div>`;

    _renderBadges();
}

function _renderBadges() {
    const el = document.getElementById('staff-badges');
    if (!el) return;

    el.innerHTML = STAFF_SLOTS.map((slot, i) => {
        if (slot.locked) {
            const isFlipped = _flipBadge === i;
            return `
                <div class="staff-badge" onclick="staffFlipBadge(${i})">
                    ${isFlipped ? `
                        <div class="staff-badge-back">
                            <div class="staff-badge-file">FILE</div>
                            <div class="staff-badge-divider"></div>
                            <div class="staff-badge-tags">Manager &amp; Owner</div>
                            <div class="staff-badge-flip-hint">↩ back</div>
                        </div>` : `
                        <div class="staff-badge-front" style="background:${slot.color}">
                            <div class="staff-badge-emoji">${slot.lockEmoji}</div>
                            <div class="staff-badge-info">
                                <div class="staff-badge-name">${slot.lockName.split(' ')[0]}</div>
                                <div class="staff-badge-role">${slot.role}</div>
                                <div class="staff-badge-owner">OWNER</div>
                            </div>
                        </div>`}
                </div>`;
        }

        const hiredName = S.staffHiredSlots[i];
        if (!hiredName) {
            return `
                <div class="staff-badge staff-badge-empty">
                    <div class="staff-badge-q">?</div>
                    <div class="staff-badge-empty-role">${slot.role.toUpperCase().replace(' ', '\n')}</div>
                </div>`;
        }

        const app = STAFF_APPLICANTS.find(a => a.name === hiredName);
        if (!app) return '';
        const isFlipped = _flipBadge === i;

        return `
            <div class="staff-badge" onclick="staffFlipBadge(${i})">
                ${isFlipped ? `
                    <div class="staff-badge-back">
                        <div class="staff-badge-file">FILE</div>
                        <div class="staff-badge-divider"></div>
                        <div class="staff-badge-tags">${app.tags.join('\n')}</div>
                        <button class="staff-badge-dismiss" onclick="event.stopPropagation();staffDismiss(${i})">✕ Release</button>
                        <div class="staff-badge-flip-hint">↩ back</div>
                    </div>` : `
                    <div class="staff-badge-front" style="background:${slot.color}">
                        <div class="staff-badge-emoji">${app.emoji}</div>
                        <div class="staff-badge-info">
                            <div class="staff-badge-name">${app.name.split(' ')[0]}</div>
                            <div class="staff-badge-role">${slot.role}</div>
                        </div>
                    </div>`}
            </div>`;
    }).join('');
}

// ── exports ────────────────────────────────────────────────────────────
window.openStaff       = openStaff;
window.buildStaff      = buildStaff;
window.staffDecide     = staffDecide;
window.staffFlipBadge  = staffFlipBadge;
window.staffDismiss    = staffDismiss;
